import React from "react"
import { cn } from "@/lib/utils"
import { ArrowRightIcon, CheckCircleIcon, FileTextIcon, VideoIcon } from "lucide-react"

interface Submission {
  id: string
  founderName: string
  founderAvatar?: string
  activity: string
  type: "module" | "interview" | "assignment"
  timestamp: string
  status?: "completed" | "pending" | "reviewed"
}

interface RecentSubmissionsListProps {
  /**
   * List of recent submissions
   */
  submissions?: Submission[]

  /**
   * View all link URL
   */
  viewAllHref?: string

  /**
   * Title
   */
  title?: string

  /**
   * Additional CSS classes
   */
  className?: string
}

const defaultSubmissions: Submission[] = []

const getActivityIcon = (type: Submission["type"]) => {
  switch (type) {
    case "module":
      return VideoIcon
    case "interview":
      return FileTextIcon
    case "assignment":
      return FileTextIcon
    default:
      return CheckCircleIcon
  }
}

const getStatusColor = (status?: Submission["status"]) => {
  switch (status) {
    case "completed":
      return "text-green-600 dark:text-green-500"
    case "pending":
      return "text-amber-600 dark:text-amber-500"
    case "reviewed":
      return "text-primary dark:text-primary"
    default:
      return "text-muted-foreground"
  }
}

export function RecentSubmissionsList({
  submissions = defaultSubmissions,
  viewAllHref = "/admin/submissions",
  title = "Recent Submissions",
  className
}: RecentSubmissionsListProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Latest founder activities
          </p>
        </div>
        <a
          href={viewAllHref}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
        >
          View All
          <ArrowRightIcon className="w-4 h-4" />
        </a>
      </div>

      {/* Submissions list */}
      <div className="space-y-3">
        {submissions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No recent submissions</p>
          </div>
        ) : (
          submissions.map((submission) => {
            const Icon = getActivityIcon(submission.type)
            return (
              <div
                key={submission.id}
                className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                {/* Avatar */}
                {submission.founderAvatar ? (
                  <img src={submission.founderAvatar} alt={submission.founderName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                    {submission.founderName?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {submission.founderName}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {submission.activity}
                      </p>
                    </div>
                    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {submission.timestamp}
                    </p>
                    {submission.status && (
                      <>
                        <span className="text-xs text-muted-foreground">•</span>
                        <p className={cn("text-xs font-medium capitalize", getStatusColor(submission.status))}>
                          {submission.status}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}